import { Chip, Menu, Stack, TableCell, TableRow, Typography } from "@mui/material";
import { DraftsOutlined, MailOutlined, MoreHoriz, Reply, UnfoldLess, UnfoldMore } from "@mui/icons-material";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import React, { useState } from "react";
import { Conversation } from "../../types/Messaging/Conversation";
import CaseMessage from "./CaseMessage";
import Button from "@mui/material/Button";
import apiClient from "../../services/ApiClient";
import { translate } from "../../services/Translations/translatorService";
import { markMessagesAsRead } from "../../services/messagingService";
import { useCurrentUser } from "../../provider/CurrentUserProvider";
import { useSnackbar } from "notistack";
import { queryKeys } from "../../services/ReactQuery/reactQueryService";
import { useQueryClient } from "@tanstack/react-query";
import { getMediaObjectFilters } from "../../services/mediaObjectService";
import ChangeMessageVisibilityModal from "./ChangeMessageVisibilityModal";
import { useDrawer } from "../../provider/DrawerProvider";
import { ProductUnion } from "../../types/ProductUnion";
import dayjs from "dayjs";

function CaseMessagesConversation({
  conversation,
  refetchConversations,
  product,
}: {
  conversation: Conversation;
  refetchConversations: Function;
  product: ProductUnion;
}) {
  const [actionMenuOpen, setActionMenuOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [openVisibilityModal, setOpenVisibilityModal] = useState(false);
  const allMessagesRead = conversation.containedMessages?.every((message) => message.readByMe);
  const currentUser = useCurrentUser();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const { setDrawerState } = useDrawer();

  const sortedMessages = conversation.containedMessages?.sort((a, b) => {
    return dayjs(b.created).unix() - dayjs(a.created).unix();
  });

  async function createPdfFromConversation() {
    let messageText = "Dokument wurde erfolgreich erstellt";
    try {
      await apiClient.post("lb/services/generate_file_from_conversation", {
        body: JSON.stringify({
          productId: product.id,
          conversationId: conversation.id,
          createdBy: currentUser.person.fullname,
        }),
      });

      const mediaObjectsFilter = getMediaObjectFilters(product, currentUser);
      await queryClient.invalidateQueries({
        queryKey: queryKeys.collection("media_objects", mediaObjectsFilter)
      });
    } catch (e) {
      messageText = "Dokument konnte nicht erstellt werden";
    }

    enqueueSnackbar(messageText, {
      variant: "custom",
      isNonInteractive: true,
      anchorOrigin: {
        vertical: "bottom",
        horizontal: "center",
      },
    });
  }

  return (
    <>
      <TableRow sx={expanded ? { "& td": { borderBottom: "none" } } : null}>
        <TableCell padding={"checkbox"}>
          <Stack
            sx={{
              alignItems: "center",
            }}
          >
            {allMessagesRead ? <DraftsOutlined color={"secondary"} /> : <MailOutlined color={"secondary"} />}
            {conversation.channel.type === "internalPlusLegalExpenseInsurance" && <Chip label={"RSV"} />}
          </Stack>
        </TableCell>
        <TableCell>
          <Typography
            sx={{
              fontWeight: allMessagesRead ? "normal" : "bold",
            }}
          >
            {conversation.subject}
          </Typography>
          {conversation.latestMessageCreatedDate !== conversation.created && (
            <Typography>
              Neuste Nachricht: {dayjs(conversation.latestMessageCreatedDate).format("DD.MM.YYYY, HH:mm")} Uhr
            </Typography>
          )}
        </TableCell>
        <TableCell align={"right"}>
          <Typography>{dayjs(conversation.created).format("DD.MM.YYYY, HH:mm") + " Uhr"}</Typography>
        </TableCell>
        <TableCell align={"right"}>
          <IconButton
            onClick={(event) => {
              setAnchorEl(event.currentTarget);
              setActionMenuOpen(!actionMenuOpen);
            }}
            size="medium"
          >
            <MoreHoriz />
          </IconButton>
          <Menu
            open={actionMenuOpen}
            anchorEl={anchorEl}
            onClose={() => {
              setActionMenuOpen(false);
            }}
          >
            {!currentUser.isExternal && (
              <MenuItem
                onClick={() => {
                  setActionMenuOpen(false);
                  setOpenVisibilityModal(true);
                }}
              >
                Sichtbarkeit ändern
              </MenuItem>
            )}
            <MenuItem
              onClick={() => {
                setActionMenuOpen(false);
                apiClient
                  .patch("/conversations/" + conversation.id, {
                    body: JSON.stringify({
                      isClosed: !conversation.isClosed,
                    }),
                  })
                  .then(() => {
                    refetchConversations();
                  });
              }}
            >
              {conversation.isClosed ? "Unterhaltung öffnen" : "Unterhaltung schließen"}
            </MenuItem>
            {!currentUser.isExternal && (
              <MenuItem
                onClick={() => {
                  setActionMenuOpen(false);
                  createPdfFromConversation();
                }}
              >
                Konversation als PDF speichern
              </MenuItem>
            )}
          </Menu>
          <IconButton
            onClick={() => {
              setExpanded(!expanded);
              markMessagesAsRead(conversation.containedMessages || []).then(() => {
                refetchConversations();
              });
            }}
            size="medium"
          >
            {expanded ? <UnfoldLess /> : <UnfoldMore />}
          </IconButton>
        </TableCell>
      </TableRow>
      {expanded && (
        <TableRow>
          <TableCell colSpan={8} padding={"none"}>
            <Stack
              direction={"row"}
              sx={{
                justifyContent: "flex-end",
                alignItems: "end",
                marginBottom: "0.5rem",
              }}
            >
              <Button
                disabled={conversation.isClosed}
                startIcon={<Reply />}
                onClick={() => {
                  setDrawerState({
                    type: "inquiryToCustomer",
                    typeSpecificData: {
                      conversationId: conversation.id,
                    },
                  });
                }}
                fullWidth={false}
              >
                Anfrage
              </Button>
              <Button
                disabled={conversation.isClosed}
                startIcon={<Reply />}
                onClick={() => {
                  setDrawerState({
                    type: "message",
                    entityData: {
                      conversation: "/conversations/" + conversation.id,
                    },
                  });
                }}
                fullWidth={false}
              >
                Antworten
              </Button>
            </Stack>
            {conversation.isClosed && (
              <Typography
                sx={{
                  textAlign: "center",
                  fontWeight: "bold",
                }}
              >
                Diese Unterhaltung wurde beendet. Sie können nicht mehr darauf antworten.
              </Typography>
            )}
            {sortedMessages?.map((message) => {
              return <CaseMessage key={message.id} message={message} />;
            })}
            <Typography sx={{ marginBottom: "1rem" }}>
              <strong>Kategorie:</strong> {translate(`messaging.conversation.type.values.${conversation.type}`)}
            </Typography>
          </TableCell>
        </TableRow>
      )}
      <ChangeMessageVisibilityModal
        open={openVisibilityModal}
        handleClose={() => setOpenVisibilityModal(false)}
        conversation={conversation}
        refetchConversations={refetchConversations}
      />
    </>
  );
}

export default CaseMessagesConversation;
